import React, { useState, useEffect, useRef, useCallback } from 'react';
import './GlobalChat.css';

interface GlobalMessage {
  userId: number;
  username: string;
  message: string;
  timestamp: number;
}

interface Announcement {
  id: number;
  message: string;
  created_at: string;
  created_by_username: string;
}

interface Conversation {
  id: number;
  user_id_1: number;
  user_id_2: number;
  user1_username: string;
  user2_username: string;
  last_message: string | null;
  last_message_sender_id: number | null;
  last_message_at: string;
  unread_count: number;
}

interface DirectMessage {
  id: number;
  sender_id: number;
  sender_username: string;
  message: string;
  created_at: string;
  read_at: string | null;
}

interface DmPayload {
  id: number;
  conversationId: number;
  senderId: number;
  senderUsername: string;
  recipientId: number;
  message: string;
  createdAt: string;
  readAt: string | null;
}

interface GlobalChatProps {
  isOpen: boolean;
  onClose: () => void;
  socket: any;
  authState: {
    isAuthenticated: boolean;
    user: { id: string; username: string; email: string } | null;
    isGuest: boolean;
  };
  blockedUserIds?: number[];
  openConversationWith?: { recipientId: number; recipientUsername: string } | null;
}

type Tab = 'lobby' | 'messages';
type MessagesView = 'list' | 'thread';

const GlobalChat: React.FC<GlobalChatProps> = ({
  isOpen,
  onClose,
  socket,
  authState,
  blockedUserIds = [],
  openConversationWith = null
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('lobby');

  // Lobby state
  const [lobbyMessages, setLobbyMessages] = useState<GlobalMessage[]>([]);
  const [lobbyInput, setLobbyInput] = useState('');
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [announcementsCollapsed, setAnnouncementsCollapsed] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const [lobbyError, setLobbyError] = useState<string | null>(null);
  const [hasJoined, setHasJoined] = useState(false);

  // DM state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messagesView, setMessagesView] = useState<MessagesView>('list');
  const [activeConversation, setActiveConversation] = useState<{
    id: number;
    recipientId: number;
    recipientUsername: string;
  } | null>(null);
  const [dmMessages, setDmMessages] = useState<DirectMessage[]>([]);
  const [dmInput, setDmInput] = useState('');
  const [dmError, setDmError] = useState<string | null>(null);
  const [dmUnreadTotal, setDmUnreadTotal] = useState(0);

  const lobbyEndRef = useRef<HTMLDivElement>(null);
  const dmEndRef = useRef<HTMLDivElement>(null);
  const lobbyContainerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  const isAuthenticated = authState.isAuthenticated && !authState.isGuest && authState.user;
  const currentUserId = authState.user ? parseInt(authState.user.id) : null;

  // Auto-scroll lobby
  useEffect(() => {
    if (autoScroll && lobbyEndRef.current) {
      lobbyEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lobbyMessages, autoScroll]);

  // Auto-scroll DM thread
  useEffect(() => {
    if (dmEndRef.current) {
      dmEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [dmMessages]);

  // Handle lobby scroll position to detect if user scrolled up
  const handleLobbyScroll = useCallback(() => {
    const container = lobbyContainerRef.current;
    if (!container) return;
    const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
    setAutoScroll(atBottom);
  }, []);

  // Join/leave global chat when panel opens/closes
  useEffect(() => {
    if (!socket || !socket.connected || !isAuthenticated) return;

    if (isOpen && !hasJoined) {
      socket.emit('joinGlobalChat');
      setHasJoined(true);
    }

    return () => {
      // Don't leave on cleanup — we leave explicitly when panel closes
    };
  }, [isOpen, socket, isAuthenticated, hasJoined]);

  // Leave when panel closes
  useEffect(() => {
    if (!isOpen && hasJoined && socket && socket.connected) {
      socket.emit('leaveGlobalChat');
      setHasJoined(false);
    }
  }, [isOpen, hasJoined, socket]);

  // Socket listeners for global chat
  useEffect(() => {
    if (!socket || !isAuthenticated) return;

    const handleHistory = (messages: GlobalMessage[]) => {
      setLobbyMessages(messages.filter(m => !blockedUserIds.includes(m.userId)));
    };

    const handleNewMessage = (msg: GlobalMessage) => {
      if (blockedUserIds.includes(msg.userId)) return;
      setLobbyMessages(prev => [...prev, msg]);
    };

    const handleAnnouncements = (data: Announcement[]) => {
      setAnnouncements(data);
    };

    const handleOnlineCount = (count: number) => {
      setOnlineCount(count);
    };

    const handleError = (data: { message: string }) => {
      setLobbyError(data.message);
      setTimeout(() => setLobbyError(null), 4000);
    };

    socket.on('globalChatHistory', handleHistory);
    socket.on('globalMessage', handleNewMessage);
    socket.on('globalAnnouncements', handleAnnouncements);
    socket.on('globalChatOnlineCount', handleOnlineCount);
    socket.on('globalChatError', handleError);

    return () => {
      socket.off('globalChatHistory', handleHistory);
      socket.off('globalMessage', handleNewMessage);
      socket.off('globalAnnouncements', handleAnnouncements);
      socket.off('globalChatOnlineCount', handleOnlineCount);
      socket.off('globalChatError', handleError);
    };
  }, [socket, isAuthenticated, blockedUserIds]);

  // Socket listeners for DMs
  useEffect(() => {
    if (!socket || !isAuthenticated) return;

    const handleConversationsList = (data: Conversation[]) => {
      setConversations(data);
    };

    const handleDirectMessages = (data: { conversationId: number; messages: DirectMessage[] }) => {
      if (activeConversation && data.conversationId === activeConversation.id) {
        setDmMessages(data.messages);
      }
    };

    const handleConversationOpened = (data: {
      conversationId: number;
      recipientId: number;
      recipientUsername: string;
      messages: DirectMessage[];
    }) => {
      setActiveConversation({
        id: data.conversationId,
        recipientId: data.recipientId,
        recipientUsername: data.recipientUsername
      });
      setDmMessages(data.messages);
      setMessagesView('thread');
    };

    const handleDmSent = (msg: DmPayload) => {
      setDmMessages(prev => [...prev, {
        id: msg.id,
        sender_id: msg.senderId,
        sender_username: msg.senderUsername,
        message: msg.message,
        created_at: msg.createdAt,
        read_at: null
      }]);
    };

    const handleIncomingDm = (msg: DmPayload) => {
      if (blockedUserIds.includes(msg.senderId)) return;

      // If viewing this conversation, add to thread
      if (activeConversation && msg.conversationId === activeConversation.id) {
        setDmMessages(prev => [...prev, {
          id: msg.id,
          sender_id: msg.senderId,
          sender_username: msg.senderUsername,
          message: msg.message,
          created_at: msg.createdAt,
          read_at: null
        }]);
        socket.emit('markDmRead', { conversationId: msg.conversationId });
      } else {
        // Update unread count
        setDmUnreadTotal(prev => prev + 1);
      }
      // Refresh conversation list
      socket.emit('getConversations');
    };

    const handleDmError = (data: { message: string }) => {
      setDmError(data.message);
      setTimeout(() => setDmError(null), 4000);
    };

    const handleUnreadCount = (data: { count: number }) => {
      setDmUnreadTotal(data.count);
    };

    const handleMarkedRead = (_data: { conversationId: number }) => {
      // Refresh conversations to update unread counts
      socket.emit('getConversations');
      socket.emit('getDmUnreadCount');
    };

    socket.on('conversationsList', handleConversationsList);
    socket.on('directMessages', handleDirectMessages);
    socket.on('conversationOpened', handleConversationOpened);
    socket.on('dmSent', handleDmSent);
    socket.on('directMessage', handleIncomingDm);
    socket.on('dmError', handleDmError);
    socket.on('dmUnreadCount', handleUnreadCount);
    socket.on('dmMarkedRead', handleMarkedRead);

    return () => {
      socket.off('conversationsList', handleConversationsList);
      socket.off('directMessages', handleDirectMessages);
      socket.off('conversationOpened', handleConversationOpened);
      socket.off('dmSent', handleDmSent);
      socket.off('directMessage', handleIncomingDm);
      socket.off('dmError', handleDmError);
      socket.off('dmUnreadCount', handleUnreadCount);
      socket.off('dmMarkedRead', handleMarkedRead);
    };
  }, [socket, isAuthenticated, activeConversation, blockedUserIds]);

  // Load conversations & unread count when Messages tab is active
  useEffect(() => {
    if (activeTab === 'messages' && socket && socket.connected && isAuthenticated) {
      socket.emit('getConversations');
      socket.emit('getDmUnreadCount');
    }
  }, [activeTab, socket, isAuthenticated]);

  // Periodically refresh unread count
  useEffect(() => {
    if (!socket || !socket.connected || !isAuthenticated || !isOpen) return;
    socket.emit('getDmUnreadCount');
    const interval = setInterval(() => {
      if (socket.connected) socket.emit('getDmUnreadCount');
    }, 30000);
    return () => clearInterval(interval);
  }, [socket, isAuthenticated, isOpen]);

  // Open a specific conversation when triggered externally (e.g. from Players page)
  useEffect(() => {
    if (!openConversationWith || !socket || !socket.connected || !isAuthenticated) return;
    setActiveTab('messages');
    socket.emit('startConversation', { recipientId: openConversationWith.recipientId });
  }, [openConversationWith, socket, isAuthenticated]);

  const sendLobbyMessage = () => {
    if (!socket || !lobbyInput.trim()) return;
    socket.emit('sendGlobalMessage', { message: lobbyInput.trim() });
    setLobbyInput('');
  };

  const sendDm = () => {
    if (!socket || !dmInput.trim() || !activeConversation) return;
    socket.emit('sendDirectMessage', {
      recipientId: activeConversation.recipientId,
      message: dmInput.trim()
    });
    setDmInput('');
  };

  const openConversation = (conv: Conversation) => {
    const recipientId = conv.user_id_1 === currentUserId ? conv.user_id_2 : conv.user_id_1;
    const recipientUsername = conv.user_id_1 === currentUserId ? conv.user2_username : conv.user1_username;
    setActiveConversation({ id: conv.id, recipientId, recipientUsername });
    setMessagesView('thread');
    socket.emit('getDirectMessages', { conversationId: conv.id });
  };

  const backToConversationList = () => {
    setMessagesView('list');
    setActiveConversation(null);
    setDmMessages([]);
    socket.emit('getConversations');
  };

  const formatTime = (ts: number | string) => {
    const date = typeof ts === 'number' ? new Date(ts) : new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <div className="global-chat-panel">
      {/* Header */}
      <div className="gc-header">
        <div className="gc-header-tabs">
          <button
            className={`gc-tab ${activeTab === 'lobby' ? 'active' : ''}`}
            onClick={() => setActiveTab('lobby')}
          >
            Lobby
          </button>
          <button
            className={`gc-tab ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => { setActiveTab('messages'); setMessagesView('list'); }}
          >
            Messages
            {dmUnreadTotal > 0 && (
              <span className="gc-unread-badge">{dmUnreadTotal > 99 ? '99+' : dmUnreadTotal}</span>
            )}
          </button>
        </div>
        <div className="gc-header-right">
          {activeTab === 'lobby' && (
            <span className="gc-online-count">{onlineCount} online</span>
          )}
          <button
            className="gc-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
      </div>

      {!isAuthenticated ? (
        <div className="gc-auth-prompt">
          <p>Sign in to chat</p>
        </div>
      ) : activeTab === 'lobby' ? (
        /* ===== LOBBY TAB ===== */
        <div className="gc-lobby">
          {/* Announcements */}
          {announcements.length > 0 && (
            <div className="gc-announcements">
              <button
                className="gc-announcements-toggle"
                onClick={() => setAnnouncementsCollapsed(!announcementsCollapsed)}
              >
                📌 Announcements {announcementsCollapsed ? '▸' : '▾'}
              </button>
              {!announcementsCollapsed && (
                <div className="gc-announcements-list">
                  {announcements.map(a => (
                    <div key={a.id} className="gc-announcement-item">
                      {a.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          <div
            className="gc-messages"
            ref={lobbyContainerRef}
            onScroll={handleLobbyScroll}
          >
            {lobbyMessages.length === 0 ? (
              <div className="gc-empty">No messages yet. Say hello!</div>
            ) : (
              lobbyMessages.map((msg, i) => (
                <div
                  key={i}
                  className={`gc-message ${msg.userId === currentUserId ? 'own' : ''}`}
                >
                  <span className="gc-msg-user">{msg.username}</span>
                  <span className="gc-msg-text">{msg.message}</span>
                  <span className="gc-msg-time">{formatTime(msg.timestamp)}</span>
                </div>
              ))
            )}
            <div ref={lobbyEndRef} />
          </div>

          {!autoScroll && (
            <button
              className="gc-scroll-bottom"
              onClick={() => {
                setAutoScroll(true);
                lobbyEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              ↓ New messages
            </button>
          )}

          {lobbyError && <div className="gc-error">{lobbyError}</div>}

          {/* Input */}
          <div className="gc-input-area">
            <input
              type="text"
              value={lobbyInput}
              onChange={e => setLobbyInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendLobbyMessage(); } }}
              placeholder="Type a message..."
              maxLength={250}
              className="gc-input"
            />
            <button
              className="gc-send-btn"
              onClick={sendLobbyMessage}
              disabled={!lobbyInput.trim()}
            >
              Send
            </button>
          </div>
        </div>
      ) : (
        /* ===== MESSAGES TAB ===== */
        <div className="gc-messages-tab">
          {messagesView === 'list' ? (
            /* Conversation List */
            <div className="gc-conv-list">
              {conversations.length === 0 ? (
                <div className="gc-empty">No conversations yet</div>
              ) : (
                conversations.map(conv => {
                  const isUser1 = conv.user_id_1 === currentUserId;
                  const otherUsername = isUser1 ? conv.user2_username : conv.user1_username;
                  return (
                    <div
                      key={conv.id}
                      className={`gc-conv-item ${conv.unread_count > 0 ? 'unread' : ''}`}
                      onClick={() => openConversation(conv)}
                    >
                      <div className="gc-conv-avatar">{otherUsername.charAt(0).toUpperCase()}</div>
                      <div className="gc-conv-info">
                        <div className="gc-conv-name">{otherUsername}</div>
                        <div className="gc-conv-preview">
                          {conv.last_message
                            ? (conv.last_message.length > 40
                                ? conv.last_message.substring(0, 40) + '...'
                                : conv.last_message)
                            : 'No messages yet'}
                        </div>
                      </div>
                      <div className="gc-conv-meta">
                        <span className="gc-conv-time">{formatTime(conv.last_message_at)}</span>
                        {conv.unread_count > 0 && (
                          <span className="gc-conv-unread">{conv.unread_count}</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            /* Conversation Thread */
            <div className="gc-thread">
              <div className="gc-thread-header">
                <button className="gc-thread-back" onClick={backToConversationList}>
                  ←
                </button>
                <span className="gc-thread-name">
                  {activeConversation?.recipientUsername}
                </span>
              </div>

              <div className="gc-thread-messages">
                {dmMessages.length === 0 ? (
                  <div className="gc-empty">Start the conversation!</div>
                ) : (
                  dmMessages.map((msg, i) => (
                    <div
                      key={msg.id || i}
                      className={`gc-dm ${msg.sender_id === currentUserId ? 'own' : ''}`}
                    >
                      <div className="gc-dm-bubble">
                        <span className="gc-dm-text">{msg.message}</span>
                        <span className="gc-dm-time">{formatTime(msg.created_at)}</span>
                      </div>
                    </div>
                  ))
                )}
                <div ref={dmEndRef} />
              </div>

              {dmError && <div className="gc-error">{dmError}</div>}

              <div className="gc-input-area">
                <input
                  type="text"
                  value={dmInput}
                  onChange={e => setDmInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendDm(); } }}
                  placeholder="Type a message..."
                  maxLength={500}
                  className="gc-input"
                />
                <button
                  className="gc-send-btn"
                  onClick={sendDm}
                  disabled={!dmInput.trim()}
                >
                  Send
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalChat;
